/**
 * English is the source of truth for every message key.
 *
 * These keys are the bundled fallback catalog (PLAN.md §17): the funnel renders
 * in the user's language even when the Tolgee CDN is unreachable. Translations
 * are authored in Tolgee and exported back into the sibling files; the four
 * non-English catalogs are typed against this one so a missing key fails the
 * build rather than silently showing English.
 */
export const en = {
  'common.needHelp': 'Need help?',
  'common.continue': 'Continue',
  'common.back': 'Back',

  'shell.cookiePolicy': 'Cookie Policy',
  'shell.privacyPolicy': 'Privacy Policy',
  'shell.cookiePreferences': 'Cookie preferences',
  'shell.language': 'Language',

  'login.title': 'Sign in',
  'login.subtitle': 'Use the email address on your account.',
  'login.forgot': 'Forgot your password?',
  'login.createAccount': 'Create an account',

  'forgot.title': 'Reset your password',
  'forgot.subtitle': "We'll email you a 6-digit code to set a new one.",
  'forgot.newTitle': 'Choose a new password',
  'forgot.backToSignIn': 'Back to sign in',

  'consent.message':
    'We use cookies to run this site and, with your consent, to measure and improve it.',
  'consent.acceptAll': 'Accept all',
  'consent.essentialOnly': 'Essential only',

  // Shared
  'common.email': 'Email address',
  'common.password': 'Password',
  'common.tryAgain': 'Try again',
  'err.tooManyAttempts': 'Too many attempts',
  'err.checkFailed': 'Check failed',
  'err.otpDeliveryFailed': "We couldn't send the code",
  'err.otpLocked': 'Too many wrong codes',
  'err.otpExpired': 'That code has expired',
  'err.countryNotPermitted': 'Not available in your country',
  'err.temporarilyUnavailable': 'Temporarily unavailable',
  'err.checkAnswers': 'Check your answers',
  'err.somethingWrong': 'Something went wrong',

  // Step indicator
  'step.account': 'Account',
  'step.identity': 'Identity',
  'step.compliance': 'Compliance',
  'step.finish': 'Finish',

  // Step 1 — account
  'account.emailTitle': 'Open your account',
  'account.emailSubtitle': "We'll email you a 6-digit code to confirm your address.",
  'account.botFailedTitle': "The bot check didn't load",
  'account.botFailedBody': "You can still continue — we'll confirm it when you submit.",
  'account.send': 'Send code',
  'account.sending': 'Sending…',
  'account.haveAccount': 'Already have an account?',
  'account.login': 'Log in',
  'account.otpTitle': 'Enter your code',
  'account.otpSentTo': 'Sent to {target}.',
  'account.otpTargetFallback': 'your email address',
  'account.escalated': "We've sent another code from a different provider.",
  'account.startAgain': 'Start again',
  'account.checking': 'Checking…',
  'account.verify': 'Verify',
  'account.resend': 'Send the code again',
  'account.tryAnotherWay': "I didn't receive it — try another way",
  'account.differentEmail': 'Use a different email address',
  'account.profileTitle': 'A few details',
  'account.profileSubtitle': 'This is the last of the account setup.',
  'account.emailReadonlyHint': 'Confirmed by the code you just entered.',
  'account.country': 'Country of residence',
  'account.countrySearch': 'Search for a country',
  'account.phone': 'Phone',
  'account.consentBefore':
    'I consent to the collection and use of my personal information in accordance with the',
  'account.consentAfter':
    ', and I understand that I may be contacted about my application.',
  'account.creating': 'Creating…',
  'account.existsTitle': 'You already have an account',
  'account.signInInstead': 'Sign in instead',

  'partner.add': 'Have a partner code?',
  'partner.label': 'Partner Code / Link ID',
  'partner.confirm': 'Apply code',
  'partner.remove': 'Remove',
  'partner.close': 'Close partner code',
  'partner.referredBy': 'Referred by',
  'partner.partnerId': 'Partner ID',
  'partner.country': 'Country',
  'partner.unavailable': 'We could not check that code. Please try again.',

  // Step 2 — identity
  'identity.notReadyTitle': 'Finish your account first',
  'identity.backToStep1': 'Back to step 1',
  'identity.title': 'Verify your identity',
  'identity.subtitle':
    'Regulation requires us to confirm who you are before you can trade. It takes about two minutes.',
  'identity.startFailedTitle': "Couldn't start verification",
  'identity.step1': 'Have your passport, national ID or driving licence to hand.',
  'identity.step2': "You'll photograph the document, then take a short selfie.",
  'identity.step3': "Allow camera access when your browser asks — the check can't run without it.",
  'identity.starting': 'Opening…',
  'identity.start': 'Start verification',
  'identity.partnerNote': "You'll be taken to our verification partner and brought straight back here.",
  'identity.inProgress': "You've already started. Continuing will pick up where you left off.",
  'webview.title': 'Open this page in your browser',
  'webview.cameraWarn': "The camera usually won't work in {browser}, and identity checks need it.",
  'webview.namedBrowser': "{app}'s built-in browser",
  'webview.thisBrowser': "this app's built-in browser",
  'webview.iosInstruction': 'Tap the share icon, then Open in Safari.',
  'webview.openInChrome': 'Open in Chrome',
  'webview.androidInstruction': 'or use the menu and choose Open in browser.',
  'webview.otherInstruction': "Copy this page's address into your normal browser.",

  // Post-KYC — confirm details
  'confirm.title': 'Confirm your details',
  'confirm.subtitle':
    "We filled these in from your document — check they're right and fix anything that isn't.",
  'confirm.saveFailedTitle': "Couldn't save your details",
  'confirm.idOutstandingTitle': 'We still need your ID',
  'confirm.idOutstandingBody':
    "Your identity check isn't complete, so please type your details in below. Carry on and finish — we'll email you about the document.",
  'confirm.firstName': 'First name',
  'confirm.lastName': 'Last name',
  'confirm.dob': 'Date of birth',
  'confirm.whereLive': 'Where do you live?',
  'confirm.city': 'City',
  'confirm.state': 'State / Province',
  'confirm.saving': 'Saving…',

  // Step 3 — compliance
  'compliance.stillCheckingTitle': "We're still checking your ID",
  'compliance.backToVerification': 'Back to verification',
  'compliance.loading': 'Loading your questionnaire…',
  'compliance.loadFailedTitle': "We couldn't load the questionnaire",
  'compliance.stillStuck': 'Still stuck?',
  'compliance.title': 'A few final questions',
  'compliance.subtitle':
    'Regulation requires us to ask these before your account can be opened. Your answers are filed with your application.',
  'compliance.submitting': 'Submitting…',
  'compliance.submit': 'Submit',
  'compliance.oneQuestionLeft': 'One question still needs an answer.',
  'compliance.questionsLeft': '{count} questions still need an answer.',
  'compliance.saveAnswersFailed': "We couldn't save your answers",

  // Shared step gate
  'gate.stepToFinishTitle': "There's a step to finish first",
  'gate.continueWhereLeft': 'Continue where you left off',

  // Step 4 — finish
  'done.loadingDocs': 'Loading the last few documents…',
  'done.loadFailedTitle': "We couldn't load your documents",
  'done.tryAgainMoment': 'Please try again in a moment.',
  'done.title': 'One last thing',
  'done.readyToSubmit': 'Your application is ready to submit.',
  'done.pleaseAccept': 'Please read and accept these before we open your account.',
  'done.agreeAll': "I've read and agree to all {count} documents",
  'done.hideDocs': 'Hide the documents',
  'done.showDocs': 'Show the {count} documents',
  'done.version': 'Version {version}',
  'done.finishing': 'Finishing…',
  'done.finishSignIn': 'Finish and sign in',
  'done.oneLeft': 'One document still needs your acceptance.',
  'done.docsLeft': '{count} documents still need your acceptance.',
  'done.straightIn': "We'll take you straight into your account — no need to sign in again.",
  'done.completeFailed': "We couldn't complete your application",
  'done.acceptEvery': 'Please accept every document',
  'done.getApps': 'Get the trading apps',
  'done.getAppsSubtitle': "You'll trade through MT5 or the KCM Trade app once your account is open.",
  'done.onComputer': 'On your computer?',
  'done.scanQr': 'Scan a QR code',
  'done.scanQrSuffix': 'to get the app on your phone.',

  // KYC outcomes
  'kyc.approvedTitle': 'Identity confirmed',
  'kyc.approvedBody': "Thanks — that's the hard part done.",
  'kyc.reviewTitle': "We're reviewing your documents",
  'kyc.reviewBody':
    "A person is checking them — usually a few hours, and we'll email you the moment it's done. No need to wait here: carry on and finish the rest now.",
  'kyc.questions': 'Questions?',
  'kyc.declinedTitle': "We couldn't verify your identity",
  'kyc.contactPrefix': 'Contact',
  'kyc.contactSuffix': "and we'll take it from there.",
  'kyc.expiredTitle': 'That session timed out',
  'kyc.expiredBody':
    "Verification sessions don't stay open for long. Starting again takes a couple of minutes.",
  'kyc.continueAnyway': 'Continue your application',
  'kyc.finishLaterBody':
    "You can finish the rest of your application now. We'll email you about your ID separately, and your account stays pending until it's sorted.",
  'decline.document_unreadable':
    "The photos of your document weren't clear enough to read. Better light and a flat surface usually fix it.",
  'decline.face_mismatch': "The selfie didn't match the photo on the document.",
  'decline.document_expired': 'The document you used has expired.',
  'decline.other': "The automated checks weren't able to confirm your identity.",

  // Didit return
  'verify.checkFailedTitle': "We couldn't check your status",
  'verify.checkFailedBody':
    'Your verification was submitted. Refreshing this page usually sorts it out.',
  'verify.refresh': 'Refresh',
  'verify.stillProcessingTitle': 'Still processing',
  'verify.stillProcessingBody':
    "This is taking longer than usual. Your documents are submitted and nothing is lost — we'll email you the moment it's decided.",
  'verify.back': 'Back',
  'verify.checkingTitle': 'Checking your documents',
  'verify.checkingBody': "This usually takes a few seconds. Please don't close this page.",
  'verify.checkingAria': 'Checking your verification',
  'handoff.receivedTitle': 'Documents received',
  'handoff.receivedBody': "Thanks — that's everything we needed from your phone.",
  'handoff.goBackTitle': 'Now go back to your computer',
  'handoff.goBackBody':
    'Your application is still open in the browser you started in. Finish it there — the page will move on by itself once your check is done.',
  'handoff.closeTab': 'You can close this tab.',
  'handoff.somethingWrong': 'Something not right?',
} as const

export type MessageKey = keyof typeof en
export type Messages = Record<MessageKey, string>
